import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * CRITICAL FIX: The original code had a conflict between the CSS removal animation
 * and Shopify's DOM morphing. When a user clicked "remove":
 *   1. The CSS animation started (row sliding out)
 *   2. The fetch request was sent to Shopify
 *   3. The fetch response arrived MID-ANIMATION
 *   4. morphSection() replaced the DOM, UNDOING the animation
 *   5. The item briefly reappeared before being morphed out
 *   6. This cycle could repeat, requiring multiple clicks
 *
 * Solution: #pendingRemovalAnimation is a Promise that resolves when the
 * CSS animation completes. The morph waits for this promise before running,
 * so the animation finishes smoothly, THEN the morph applies the server state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {AbortController|null} Cancels in-flight requests when a new one starts */
  #abortController = null;

  /** @type {Promise<void>|null} Resolves when the removal animation finishes */
  #pendingRemovalAnimation = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#abortController?.abort();
    this.#abortController = null;
    this.#pendingRemovalAnimation = null;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows?.[line - 1];
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   *
   * KEY FIX: Creates a Promise (#pendingRemovalAnimation) that the updateQuantity
   * response handler awaits before calling morphSection(). This prevents the morph
   * from undoing the CSS animation mid-flight.
   *
   * @param {number} line - The line item index (1-based).
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows?.[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines (bundle children) of the row to remove
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key
      ),
    ];

    // Create a promise that resolves when ALL removal animations complete.
    // updateQuantity() will await this before morphing the DOM.
    this.#pendingRemovalAnimation = new Promise((resolve) => {
      let animationsRemaining = rowsToRemove.length;

      if (animationsRemaining === 0) {
        resolve();
        return;
      }

      rowsToRemove.forEach((row) => {
        const onComplete = () => {
          row.remove();
          animationsRemaining--;
          if (animationsRemaining <= 0) {
            resolve();
          }
        };

        if (prefersReducedMotion()) {
          onComplete();
          return;
        }

        row.style.setProperty('--row-height', `${row.clientHeight}px`);
        row.classList.add('removing');
        onAnimationEnd(row, onComplete);
      });

      // Safety: if the animation somehow never fires onAnimationEnd
      // (e.g., element is detached, or animation is none), resolve after 1.5s
      setTimeout(() => {
        if (animationsRemaining > 0) {
          rowsToRemove.forEach((row) => {
            try { row.remove(); } catch (e) { /* already removed */ }
          });
          resolve();
        }
      }, 1500);
    });

    // Send the removal request to Shopify (runs in parallel with animation)
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity via the Shopify Cart API.
   *
   * For 'clear' (removal) actions, the morph is deferred until the removal
   * animation completes (via #pendingRemovalAnimation).
   * For 'change' (quantity update) actions, the morph runs immediately.
   *
   * @param {Object} config - The config.
   * @param {number} config.line - The line number (1-based).
   * @param {number} config.quantity - The new quantity.
   * @param {string} config.action - The action type ('change' | 'clear').
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    // Abort any previous in-flight request to prevent race conditions
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    // Collect all cart-items sections that need to be re-rendered
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, {
      ...fetchConfig('json', { body }),
      signal: this.#abortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          // If there was a removal animation pending, let it finish but don't morph
          this.#pendingRemovalAnimation = null;
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants across the page
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        /**
         * Apply the morph — with coordination for removal animations.
         *
         * If a removal animation is in progress, we MUST wait for it to complete
         * before morphing. Otherwise, the morph replaces the DOM mid-animation,
         * undoing the visual removal and making the item "reappear."
         */
        const applyMorph = () => {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
          this.#updateCartQuantitySelectorButtonStates();
        };

        if (this.#pendingRemovalAnimation) {
          const animationPromise = this.#pendingRemovalAnimation;
          this.#pendingRemovalAnimation = null;
          animationPromise.then(applyMorph);
        } else {
          applyMorph();
        }
      })
      .catch((error) => {
        // Ignore AbortError — expected when a new request supersedes an old one
        if (error.name === 'AbortError') return;
        console.error('[CartItemsComponent] Update failed:', error);
        this.#pendingRemovalAnimation = null;
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles cart errors gracefully without throwing.
   * Reverts the quantity input and shows the error message.
   *
   * @param {number} line - The line number (1-based).
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The error message from Shopify.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) {
      console.error('[CartItemsComponent] Quantity input not found for line', line);
      return;
    }

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
    }

    if (cartItemErrorContainer instanceof HTMLElement) {
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  /**
   * Handles the cart update event from other components.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];

    // If a removal animation is in progress, wait for it before morphing
    const applyUpdate = () => {
      if (cartItemsHtml) {
        morphSection(this.sectionId, cartItemsHtml);
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
    };

    if (this.#pendingRemovalAnimation) {
      const animationPromise = this.#pendingRemovalAnimation;
      this.#pendingRemovalAnimation = null;
      animationPromise.then(applyUpdate);
    } else {
      applyUpdate();
    }
  };

  /**
   * Disables cart items during an update.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Re-enables cart items after an update completes.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants across the page.
   *
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id from the data attribute.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

